import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    AppstoreOutlined,
    ArrowLeftOutlined,
    BankOutlined,
    SearchOutlined,
    TableOutlined,
    RightOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Button,
    Card,
    Col,
    ConfigProvider,
    Empty,
    Input,
    Modal,
    Progress,
    Row,
    Segmented,
    Select,
    Spin,
    Table,
    Tabs,
    Tag,
    Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";

const PRIMARY = "#242166";

const pct = (used, total) => (total > 0 ? Number(((used * 100) / total).toFixed(1)) : 0);

const strokeFor = (percent) => {
    if (percent >= 80) return "#dc2626";
    if (percent >= 50) return "#f59e0b";
    return "#16a34a";
};

const nb = (v) => Number(v || 0).toLocaleString("fr-FR");

/**
 * Plages de numéros ("1000-1249") en pastilles.
 *
 * Un lot dépasse couramment le millier de numéros : au-delà de MAX_VISIBLE plages,
 * le reste est replié derrière un bouton plutôt que d'inonder la modale.
 */
const PlagesNumeros = ({ plages, couleur, vide }) => {
    const MAX_VISIBLE = 60;
    const [tout, setTout] = useState(false);
    const liste = plages || [];

    if (liste.length === 0) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={vide} style={{ padding: "24px 0" }} />;
    }
    const visibles = tout ? liste : liste.slice(0, MAX_VISIBLE);
    return (
        <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {visibles.map((p) => (
                    <Tag key={p} color={couleur} style={{ margin: 0, fontFamily: "monospace", fontSize: 12 }}>
                        {p}
                    </Tag>
                ))}
            </div>
            {liste.length > MAX_VISIBLE && (
                <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => setTout(!tout)}>
                    {tout ? "Réduire" : `Afficher les ${liste.length - MAX_VISIBLE} autres plages`}
                </Button>
            )}
        </>
    );
};

/**
 * Suivi des lots d'attestations dispatchés, agence par agence, pour une compagnie.
 *
 * Vue d'ensemble (cartes ou tableau) listant chaque agence/subdivision avec la
 * situation de ses lots dispatchés (total, utilisés, restants, taux d'usage).
 * Un clic ouvre le détail des lots de l'agence (page existante).
 */
const SuiviLotsParAgence = () => {
    const navigate = useNavigate();
    const param = useParams();
    const codeCompagnie = param.codeCompagnie ?? LOGGED_USER?.profil?.organisme?.code ?? "00";

    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState([]);
    const [compagnie, setCompagnie] = useState({});
    const [search, setSearch] = useState("");
    const [viewMode, setViewMode] = useState("cards");
    const [sortBy, setSortBy] = useState("name");
    // Modale « numéros » : { agence, lots, erreur }. null = fermée.
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            axiosInstance.get(`/api/dispatched-lot-attestation/stats/subdivisions/${codeCompagnie}`),
            axiosInstance.get(`/api/organisme/code/${codeCompagnie}`).catch(() => ({ data: {} })),
        ])
            .then(([statsRes, orgRes]) => {
                setStats(Array.isArray(statsRes.data) ? statsRes.data : []);
                setCompagnie(orgRes?.data ?? {});
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    }, [codeCompagnie]);

    const agences = useMemo(() => {
        const list = stats.map((s) => {
            const total = s?.total || 0;
            const used = s?.used || 0;
            const remaining = Math.max(total - used, 0);
            return {
                code: s?.code,
                nom: s?.compagnie || s?.agence?.nom || s?.code || "—",
                total,
                used,
                remaining,
                percent: pct(used, total),
            };
        });

        const filtered = search.trim()
            ? list.filter((a) =>
                `${a.nom} ${a.code}`.toLowerCase().includes(search.trim().toLowerCase())
            )
            : list;

        const sorted = [...filtered].sort((a, b) => {
            if (sortBy === "usage") return b.percent - a.percent;
            if (sortBy === "remaining") return b.remaining - a.remaining;
            if (sortBy === "total") return b.total - a.total;
            return a.nom.localeCompare(b.nom);
        });
        return sorted;
    }, [stats, search, sortBy]);

    const totals = useMemo(() => {
        const total = agences.reduce((s, a) => s + a.total, 0);
        const used = agences.reduce((s, a) => s + a.used, 0);
        const remaining = agences.reduce((s, a) => s + a.remaining, 0);
        return { total, used, remaining, percent: pct(used, total), nbAgences: agences.length };
    }, [agences]);

    // Page de détail complète — conservée, atteignable depuis la modale.
    const openAgence = (code) => {
        navigate(`/dispatched-lots-attestations/${code}`, {
            state: { codeAgence: code, codeOrganisme: codeCompagnie },
        });
    };

    /**
     * Ouvre la modale des NUMÉROS d'une agence : consommés vs disponibles, lot par lot.
     * Chargement à la demande — l'agrégation des numéros est coûteuse côté base, on ne
     * la déclenche donc jamais au chargement de la page.
     */
    const openNumeros = (agence) => {
        setDetail({ agence, lots: [], erreur: null });
        setDetailLoading(true);
        axiosInstance
            .get(`/api/dispatched-lot-attestation/agency/${agence.code}/numeros`, {
                params: { codeCompagnie },
            })
            .then((res) => {
                setDetail({ agence, lots: Array.isArray(res.data) ? res.data : [], erreur: null });
            })
            .catch((err) => {
                console.log(err);
                setDetail({ agence, lots: [], erreur: "Impossible de charger les numéros de cette agence." });
            })
            .finally(() => setDetailLoading(false));
    };

    const summaryCard = (label, value, color) => (
        <Card size="small" bordered={false} style={{ borderRadius: 12, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value.toLocaleString("fr-FR")}</div>
        </Card>
    );

    const columns = [
        {
            title: "Agence",
            dataIndex: "nom",
            key: "nom",
            render: (nom, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 34, height: 34, borderRadius: 8, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        <BankOutlined />
                    </span>
                    <div>
                        <div style={{ fontWeight: 600, color: "#111827" }}>{nom}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>Code {r.code}</div>
                    </div>
                </div>
            ),
        },
        { title: "Total dispatché", dataIndex: "total", key: "total", align: "center", render: (v) => <strong style={{ color: PRIMARY }}>{v.toLocaleString("fr-FR")}</strong>, sorter: (a, b) => a.total - b.total },
        { title: "Utilisés", dataIndex: "used", key: "used", align: "center", render: (v) => <span style={{ color: "#dc2626" }}>{v.toLocaleString("fr-FR")}</span>, sorter: (a, b) => a.used - b.used },
        { title: "Restants", dataIndex: "remaining", key: "remaining", align: "center", render: (v) => <span style={{ color: "#16a34a" }}>{v.toLocaleString("fr-FR")}</span>, sorter: (a, b) => a.remaining - b.remaining },
        {
            title: "Taux d'usage",
            dataIndex: "percent",
            key: "percent",
            align: "center",
            sorter: (a, b) => a.percent - b.percent,
            render: (p) => <Progress percent={p} size="small" strokeColor={strokeFor(p)} style={{ width: 120, margin: "0 auto" }} />,
        },
        {
            title: "",
            key: "action",
            align: "center",
            render: (_, r) => (
                <Button type="text" style={{ color: PRIMARY }} onClick={(e) => { e.stopPropagation(); openNumeros(r); }}>
                    Numéros <RightOutlined />
                </Button>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* En-tête */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
                                    <div>
                                        <h4 style={{ margin: 0, color: "#111827" }}>Suivi des lots par agence</h4>
                                        <span style={{ color: "#6b7280", fontSize: 13 }}>
                                            Lots dispatchés et leur situation — {compagnie?.raisonSociale ?? ""}
                                        </span>
                                    </div>
                                </div>
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { value: "cards", label: <span><AppstoreOutlined /> Cartes</span> },
                                        { value: "table", label: <span><TableOutlined /> Tableau</span> },
                                    ]}
                                />
                            </div>

                            <ConfigProvider locale={frFR}>
                                <Spin spinning={loading}>
                                    {/* Bandeau récap */}
                                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                                        <Col xs={12} md={6}>{summaryCard("Agences", totals.nbAgences, PRIMARY)}</Col>
                                        <Col xs={12} md={6}>{summaryCard("Total dispatché", totals.total, PRIMARY)}</Col>
                                        <Col xs={12} md={6}>{summaryCard("Utilisés", totals.used, "#dc2626")}</Col>
                                        <Col xs={12} md={6}>{summaryCard("Restants", totals.remaining, "#16a34a")}</Col>
                                    </Row>

                                    {/* Barre d'outils : recherche + saut + tri */}
                                    <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                        <Row gutter={[12, 12]} align="middle">
                                            <Col xs={24} md={10}>
                                                <Input
                                                    allowClear
                                                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                                    placeholder="Rechercher une agence (nom ou code)…"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    style={{ width: "100%" }}
                                                    placeholder="Aller directement à une agence…"
                                                    optionFilterProp="label"
                                                    onChange={(code) => code && openAgence(code)}
                                                    options={agences.map((a) => ({ value: a.code, label: `${a.nom} (${a.code})` }))}
                                                />
                                            </Col>
                                            <Col xs={24} md={6}>
                                                <Select
                                                    style={{ width: "100%" }}
                                                    value={sortBy}
                                                    onChange={setSortBy}
                                                    options={[
                                                        { value: "name", label: "Trier : Nom" },
                                                        { value: "usage", label: "Trier : Taux d'usage" },
                                                        { value: "remaining", label: "Trier : Restants" },
                                                        { value: "total", label: "Trier : Total" },
                                                    ]}
                                                />
                                            </Col>
                                        </Row>
                                    </Card>

                                    {/* Contenu */}
                                    {agences.length === 0 ? (
                                        <Empty description="Aucun lot dispatché trouvé" style={{ padding: "60px 0" }} />
                                    ) : viewMode === "table" ? (
                                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                            <Table
                                                rowKey="code"
                                                columns={columns}
                                                dataSource={agences}
                                                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
                                                onRow={(r) => ({ onClick: () => openNumeros(r), style: { cursor: "pointer" } })}
                                            />
                                        </Card>
                                    ) : (
                                        <Row gutter={[16, 16]}>
                                            {agences.map((a) => (
                                                <Col xs={24} sm={12} lg={8} xxl={6} key={a.code}>
                                                    <Card
                                                        hoverable
                                                        onClick={() => openNumeros(a)}
                                                        bordered={false}
                                                        style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.07)", height: "100%" }}
                                                        bodyStyle={{ padding: 18 }}
                                                    >
                                                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                                                <span style={{ width: 40, height: 40, borderRadius: 10, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                                                                    <BankOutlined />
                                                                </span>
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div style={{ fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.nom}</div>
                                                                    <Tag style={{ marginTop: 2, fontSize: 11 }} color="default">Code {a.code}</Tag>
                                                                </div>
                                                            </div>
                                                            <Progress type="circle" size={52} percent={a.percent} strokeColor={strokeFor(a.percent)} />
                                                        </div>

                                                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
                                                            <div style={{ textAlign: "center", flex: 1 }}>
                                                                <div style={{ fontSize: 11, color: "#9ca3af" }}>Total</div>
                                                                <div style={{ fontWeight: 700, color: PRIMARY }}>{a.total.toLocaleString("fr-FR")}</div>
                                                            </div>
                                                            <div style={{ textAlign: "center", flex: 1, borderLeft: "1px solid #f0f0f0", borderRight: "1px solid #f0f0f0" }}>
                                                                <div style={{ fontSize: 11, color: "#9ca3af" }}>Utilisés</div>
                                                                <div style={{ fontWeight: 700, color: "#dc2626" }}>{a.used.toLocaleString("fr-FR")}</div>
                                                            </div>
                                                            <div style={{ textAlign: "center", flex: 1 }}>
                                                                <div style={{ fontSize: 11, color: "#9ca3af" }}>Restants</div>
                                                                <div style={{ fontWeight: 700, color: "#16a34a" }}>{a.remaining.toLocaleString("fr-FR")}</div>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </Spin>

                                {/* Modale : numéros consommés / disponibles, lot par lot */}
                                <Modal
                                    open={!!detail}
                                    onCancel={() => setDetail(null)}
                                    width={920}
                                    title={
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{detail?.agence?.nom}</div>
                                            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                                                Numéros d'attestations — code {detail?.agence?.code}
                                            </div>
                                        </div>
                                    }
                                    footer={[
                                        <Button key="page" onClick={() => openAgence(detail?.agence?.code)}>
                                            Ouvrir la page détaillée
                                        </Button>,
                                        <Button key="close" type="primary" onClick={() => setDetail(null)}>
                                            Fermer
                                        </Button>,
                                    ]}
                                >
                                    <Spin spinning={detailLoading}>
                                        {detail?.erreur ? (
                                            <Alert type="error" showIcon message={detail.erreur} />
                                        ) : detail?.lots?.length === 0 && !detailLoading ? (
                                            <Empty description="Aucun lot dispatché à cette agence" />
                                        ) : (
                                            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                                {(detail?.lots ?? []).map((lot) => {
                                                    const util = lot.totalUtilises || 0;
                                                    const tot = lot.totalDispatches || 0;
                                                    const p = tot > 0 ? Number(((util * 100) / tot).toFixed(1)) : 0;
                                                    return (
                                                        <Card
                                                            key={lot.codeId}
                                                            size="small"
                                                            style={{ marginBottom: 12, borderRadius: 10 }}
                                                            title={
                                                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                                    <strong style={{ color: PRIMARY }}>
                                                                        {nb(lot.debut)} → {nb(lot.fin)}
                                                                    </strong>
                                                                    {lot.serie && <Tag color="blue">Série {lot.serie}</Tag>}
                                                                    <Tag>{nb(tot)} numéros</Tag>
                                                                    {lot.totalDisponiblesHorsLot > 0 && (
                                                                        <Tooltip title={`${nb(lot.totalDisponiblesHorsLot)} numéros marqués disponibles sont hors de la plage dispatchée (${nb(lot.debut)}-${nb(lot.fin)}). Incohérence de données à corriger.`}>
                                                                            <Tag color="warning">⚠ {nb(lot.totalDisponiblesHorsLot)} hors plage</Tag>
                                                                        </Tooltip>
                                                                    )}
                                                                    <Tooltip title="Part des numéros déjà consommés">
                                                                        <Progress
                                                                            percent={p}
                                                                            size="small"
                                                                            strokeColor={strokeFor(p)}
                                                                            style={{ width: 130, margin: 0 }}
                                                                        />
                                                                    </Tooltip>
                                                                </div>
                                                            }
                                                        >
                                                            <Tabs
                                                                size="small"
                                                                items={[
                                                                    {
                                                                        key: "dispo",
                                                                        label: `Disponibles (${nb(lot.totalDisponibles)})`,
                                                                        children: (
                                                                            <PlagesNumeros
                                                                                plages={lot.plagesDisponibles}
                                                                                couleur="green"
                                                                                vide="Aucun numéro disponible — lot épuisé"
                                                                            />
                                                                        ),
                                                                    },
                                                                    {
                                                                        key: "utilises",
                                                                        label: `Utilisés (${nb(lot.totalUtilises)})`,
                                                                        children: (
                                                                            <PlagesNumeros
                                                                                plages={lot.plagesUtilisees}
                                                                                couleur="red"
                                                                                vide="Aucun numéro consommé sur ce lot"
                                                                            />
                                                                        ),
                                                                    },
                                                                    ...(lot.totalAbimes > 0
                                                                        ? [{
                                                                            key: "abimes",
                                                                            label: `Abîmés (${nb(lot.totalAbimes)})`,
                                                                            children: <PlagesNumeros plages={lot.plagesAbimees} couleur="orange" vide="—" />,
                                                                        }]
                                                                        : []),
                                                                    ...(lot.totalAnnules > 0
                                                                        ? [{
                                                                            key: "annules",
                                                                            label: `Annulés (${nb(lot.totalAnnules)})`,
                                                                            children: <PlagesNumeros plages={lot.plagesAnnulees} couleur="default" vide="—" />,
                                                                        }]
                                                                        : []),
                                                                ]}
                                                            />
                                                        </Card>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Spin>
                                </Modal>
                            </ConfigProvider>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default SuiviLotsParAgence;
